import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {ArrowRight, QuoteIcon} from 'lucide-react'
import {sanity, urlFor} from '../lib/sanity'

const placeholderImage = '/assets/images/placeholderEventImage.jpg'

const SPEECHES_QUERY = `*[
  _type == "speech" &&
  defined(slug.current)
] | order(date desc) {
  _id,
  title,
  "slug": slug.current,
  speaker,
  date,
  location,
  occasion,
  image,
  intro
}`

function formatDate(dateString) {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateString))
}

function getImageSrc(image) {
  const hasSanityImage = image?.asset?._ref || image?.asset?._id

  return hasSanityImage
    ? urlFor(image).width(900).height(900).url()
    : placeholderImage
}

function SpeechesPage() {
  const [speeches, setSpeeches] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    sanity
      .fetch(SPEECHES_QUERY)
      .then((result) => {
        setSpeeches(result || [])
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setLoading(false)
      })
  }, [])

  if (loading) return null

  return (
    <main className="min-h-screen px-4 pb-20 pt-24 text-[#5757fd] md:px-8">
      <section className="mx-auto max-w-7xl">
        <div className="mb-14 gap-10">
          <div>
            <p className="mb-4 w-fit rounded-full bg-white px-4 py-2 text-sm font-sans_bold">
              Archief
            </p>

            <h1 className="pb-4 font-sans_bold text-5xl leading-none tracking-tight md:text-7xl">
              Toespraken
            </h1>
          </div>

          <p className="max-w-xl font-sans text-lg leading-tight md:text-xl">
            Een groeiend archief van woorden uitgesproken tijdens de Nationale
            Herdenking Slavernijverleden.
          </p>
        </div>

        {speeches.length > 0 ? (
          <div className="grid gap-6">
            {speeches.map((speech) => (
              <Link
                key={speech._id}
                to={`/toespraken/${speech.slug}`}
                className="group grid overflow-hidden rounded-[3rem] bg-white/70 transition-all duration-500 hover:-translate-y-2 hover:bg-white hover:shadow-[0_18px_40px_rgba(87,87,253,0.18)] md:grid-cols-[360px_1fr]"
              >
                <div className="relative min-h-[320px] overflow-hidden">
                  <img
                    src={getImageSrc(speech.image)}
                    alt={speech.image?.alt || speech.speaker || speech.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  {speech.date && (
                    <div className="absolute right-4 top-4 bg-white px-4 py-2 text-sm font-sans_bold text-[#5757fd]">
                      {formatDate(speech.date)}
                    </div>
                  )}
                </div>

                <div className="flex flex-col justify-between p-6 text-[#5757fd] md:p-10">
                  <div>
                    <div className="mb-8 flex items-center gap-3">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f3f2ff] text-[#5757fd]">
                        <QuoteIcon className="h-5 w-5" />
                      </span>

                      {speech.occasion && (
                        <p className="font-sans_bold text-sm tracking-wide">
                          {speech.occasion}
                        </p>
                      )}
                    </div>

                    <h2 className="max-w-4xl font-sans_bold text-4xl leading-none tracking-tight md:text-6xl">
                      {speech.title}
                    </h2>

                    {speech.intro && (
                      <p className="mt-6 max-w-3xl font-sans text-xl leading-tight">
                        {speech.intro}
                      </p>
                    )}
                  </div>

                  <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                      {speech.speaker && (
                        <span className="rounded-full bg-[#f3f2ff] px-4 py-2 text-sm font-sans_bold">
                          {speech.speaker}
                        </span>
                      )}

                      {speech.location && (
                        <span className="rounded-full bg-[#f3f2ff] px-4 py-2 text-sm font-sans_bold">
                          {speech.location}
                        </span>
                      )}
                    </div>

                    <span className="flex items-center font-sans_bold">
                      Lees toespraak
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-[2rem] bg-white/60 p-12 text-center">
            <p className="font-sans text-lg">Nog geen toespraken gevonden.</p>
          </div>
        )}
      </section>
    </main>
  )
}

export default SpeechesPage